// libs
import React, { useEffect, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';

// constants
import { ARIA_LABELS } from 'shared/layouts/Coupons/constants';

// components
import { OverlaySheetContext, SHEET_TRANSITION } from 'shared/components/OverlaySheet';
import ValidityText from 'shared/layouts/Coupons/CouponsExpiry/CouponValdity/CouponValidity';

// hooks
import useKeyboardClick from 'shared/hooks/useKeyboardClick';

// context
import { useLoaderContext } from 'shared/components/LoaderProvider/context';
import { useDomainConfig } from 'shared/domainConfig/context/context';

// svgs
import CloseIcon from '@nykaa/ui-components/Icons/close';

// styles
import {
  Header,
  Title,
  Subtitle,
  InfoContainer,
  IconContainer,
  Image,
  CouponInfoDesktop,
  TermsAndContitionsDesktop,
  TACHeading,
  TACList,
  TACItem,
  BottomContainer,
  ActionContainerPDP,
  CopyButtonPDP,
  ActionButtonPDP,
  OverlaySheetDesktop,
  IconContainerDesktop as CloseButton,
  FixedContent,
  CouponCodeContainer,
  CouponCode,
  InfoContainerWrapper,
} from 'shared/layouts/Coupons/ViewDetailsNudge/styles';

// constants
import { TEXTS } from 'shared/layouts/Coupons/constants';
import { LOGO_TYPE } from 'shared/constants';

// types
import { CouponDetails } from 'shared/layouts/Coupons/types';
import Logo from 'shared/components/Logo';

// defs
interface ViewDetailsNudgeDesktopProps {
  onHide: VoidFunction;
  couponDetails: CouponDetails;
  onCopyClicked: (couponCode: string) => void;
  className?: string;
  ViewDetailsNudgeDesktopProps?: string;
  sheetTransition?: SHEET_TRANSITION;
}

function ViewDetailsNudgeDesktop({
  onHide,
  couponDetails,
  onCopyClicked,
  className = '',
  sheetTransition = SHEET_TRANSITION.RIGHT,
}: ViewDetailsNudgeDesktopProps) {
  const { showLoader, hideLoader } = useLoaderContext();
  const closeKeyboardProps = useKeyboardClick(onHide);
  const closeButtonRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const {
    COLOR: { COUPON_CODE_BACKGROUND },
  } = useDomainConfig();

  useEffect(() => {
    if (closeButtonRef.current) {
      closeButtonRef.current.focus();
    }

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !modalRef.current) return;

      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex^="-"])'
      );

      const focusableArray = Array.from(focusableElements);
      if (focusableArray.length === 0) return;

      const firstElement = focusableArray[0];
      const lastElement = focusableArray[focusableArray.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, []);

  if (isEmpty(couponDetails)) {
    showLoader({ right: '14%', left: 'none' });
    return null;
  } else hideLoader();

  const { couponCode, imageUrl, title, subtitle, tnc, expiryEpoch = 0 } = couponDetails;

  return (
    <OverlaySheetDesktop
      onHide={onHide}
      transitionFrom={sheetTransition}
      className={className}
      navKey={'coupon-dweb'}
    >
      <OverlaySheetContext.Consumer>
        {({ doHide }) => (
          <FixedContent ref={modalRef}>
            <Header>
              <CloseButton
                ref={closeButtonRef}
                {...closeKeyboardProps}
                aria-label={ARIA_LABELS.CLOSE_COUPON_DETAILS}
              >
                <CloseIcon aria-hidden="true" />
              </CloseButton>
            </Header>
            <InfoContainerWrapper>
              <InfoContainer showBottomBorder className="infoContainer">
                <IconContainer>
                  {imageUrl === '' || imageUrl === null ? (
                    <Logo type={LOGO_TYPE.COUPONS} />
                  ) : (
                    <Image src={imageUrl} width={66} height={66} alt="coupon logo" />
                  )}
                </IconContainer>
                <CouponInfoDesktop>
                  <Title>{title}</Title>
                  <Subtitle>{subtitle}</Subtitle>
                  {expiryEpoch > 0 && <ValidityText expiryEpoch={expiryEpoch} />}
                </CouponInfoDesktop>
              </InfoContainer>
              {tnc && tnc.length > 0 && (
                <TermsAndContitionsDesktop>
                  <TACHeading>{TEXTS.TERMS_AND_CONDITIONS}</TACHeading>
                  <TACList className="termsConditionsList">
                    {tnc.map((item, i) => (
                      <TACItem key={`tnc-item-${i}`}>{item}</TACItem>
                    ))}
                  </TACList>
                </TermsAndContitionsDesktop>
              )}
            </InfoContainerWrapper>
            <BottomContainer showTopBorder={false} isDesktop={true}>
              <CouponCodeContainer
                isApplied={false}
                className="couponCodeContainer"
                bgColor={COUPON_CODE_BACKGROUND}
              >
                <CouponCode className="couponCode">{couponCode}</CouponCode>
              </CouponCodeContainer>
              <ActionContainerPDP className="actionContainer">
                <ActionButtonPDP
                  onClick={() => onHide()}
                  tabIndex={0}
                  aria-label={ARIA_LABELS.DISMISS_COUPON_DETAILS}
                >
                  {TEXTS.DISMISS}
                </ActionButtonPDP>
                <CopyButtonPDP
                  onClick={() => onCopyClicked(couponCode || '')}
                  disabled={false}
                  tabIndex={0}
                  aria-label={ARIA_LABELS.COPY_COUPON_CODE(couponCode || '')}
                >
                  {TEXTS.COPY_CODE}
                </CopyButtonPDP>
              </ActionContainerPDP>
            </BottomContainer>
          </FixedContent>
        )}
      </OverlaySheetContext.Consumer>
    </OverlaySheetDesktop>
  );
}

export default ViewDetailsNudgeDesktop;
